import styled from "@emotion/styled";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { AppBar, Box, Button, IconButton, Toolbar } from "@mui/material";

interface NavigationMenuButtonProps {
	isSelected?: boolean;
}

export const NavigationAppBar = styled(AppBar)(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.main,
	borderBottom: theme.project.borders.presets.thinOutline,
	boxShadow: "none",
	color: theme.project.colors.fontColors.main,
	overflow: "visible",
	position: "sticky",
	top: "var(--tandem-notice-height, 0px)"
}));

export const NavigationToolbar = styled(Toolbar)(() => ({
	alignItems: "center",
	display: "flex",
	gap: 16,
	justifyContent: "space-between",
	minHeight: 72,
	padding: "0 24px"
}));

export const BrandButton = styled("button")({
	alignItems: "center",
	background: "transparent",
	border: 0,
	cursor: "pointer",
	display: "inline-flex",
	flexShrink: 0,
	margin: 0,
	padding: 0
});

export const BrandImage = styled("img")({
	display: "block",
	height: 32,
	objectFit: "contain",
	width: "auto"
});

export const NavigationControls = styled(Box)({
	alignItems: "center",
	display: "flex",
	flex: "1 1 auto",
	gap: 12,
	justifyContent: "flex-end",
	minWidth: 0
});

export const NavigationMenus = styled(Box)({
	alignItems: "center",
	display: "flex",
	flex: "0 1 auto",
	gap: 8,
	minWidth: 0,
	justifyContent: "flex-end",
	overflowX: "auto"
});

export const NavigationMenuButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== "isSelected"
})<NavigationMenuButtonProps>(({ theme, isSelected }) => ({
	borderRadius: theme.project.borders.radius.small,
	color: isSelected ? theme.project.colors.primaryColors.main : theme.project.colors.fontColors.main,
	flexShrink: 0,
	fontWeight: isSelected ? 700 : 400,
	padding: "10px 14px",
	textTransform: "none",
	whiteSpace: "nowrap",
	"&:hover": {
		backgroundColor: theme.palette.grey[100]
	}
}));

export const NavigationButtonLabel = styled("span")(({ theme }) => ({
	...theme.project.typography.body.body2,
	fontWeight: "inherit"
}));

export const PrimaryActions = styled(Box)({
	alignItems: "center",
	display: "flex",
	flexShrink: 0,
	gap: 8,
	justifyContent: "flex-end"
});

export const PrimaryActionButton = styled(Button)(({ theme }) => ({
	backgroundColor: theme.project.colors.secondaryColors.action,
	color: theme.project.colors.secondaryColors.primary,
	fontWeight: 600,
	padding: "10px 16px",
	textTransform: "none",
	whiteSpace: "nowrap",
	"&:hover": {
		backgroundColor: theme.project.colors.secondaryColors["action:hover"]
	},
	radius: {
		small: 0,
		large: 0,
		extraLarge: 0
	}
}));

export const MobileNavigationBar = styled(Box)({
	alignItems: "center",
	display: "flex",
	gap: 12,
	justifyContent: "space-between",
	width: "100%"
});

export const MobileNavigationLeading = styled(Box)({
	alignItems: "center",
	display: "flex",
	flex: "1 1 auto",
	gap: 12,
	minWidth: 0
});

export const HamburgerButton = styled(IconButton)(({ theme }) => ({
	borderRadius: theme.project.borders.radius.small,
	color: theme.project.colors.fontColors.main
}));

export const MobileNavigationPanel = styled(Box)(({ theme }) => ({
	backgroundColor: theme.project.colors.backgroundColors.main,
	borderTop: theme.project.borders.presets.thinOutline,
	boxShadow: theme.shadows[4],
	display: "flex",
	flexDirection: "column",
	gap: 16,
	left: 0,
	padding: 16,
	position: "absolute",
	right: 0,
	top: "100%",
	width: "100%"
}));

export const MobileNavigationSection = styled(Box)({
	display: "flex",
	flexDirection: "column",
	gap: 4
});

export const MobileNavigationSectionButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== "isSelected"
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
	borderRadius: theme.project.borders.radius.small,
	color: isSelected ? theme.project.colors.primaryColors.main : theme.project.colors.fontColors.main,
	fontWeight: isSelected ? 700 : 500,
	justifyContent: "space-between",
	padding: "10px 16px 10px 12px",
	textTransform: "none",
	width: "100%"
}));

export const MobileNavigationSectionLabel = styled("span")(({ theme }) => ({
	...theme.project.typography.subtitle.subtitle2,
	fontWeight: "inherit"
}));

export const MobileNavigationChevron = styled(ChevronRightRoundedIcon, {
	shouldForwardProp: (prop) => prop !== "isOpen"
})<{ isOpen?: boolean }>(({ isOpen, theme }) => ({
	color: theme.project.colors.fontColors.secondary,
	flexShrink: 0,
	marginRight: 4,
	transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
	transition: "transform 200ms ease"
}));

export const MobileNavigationChildren = styled(Box)({
	display: "flex",
	flexDirection: "column",
	gap: 4,
	paddingLeft: 8
});

export const MobileNavigationItemButton = styled(Button)(({ theme }) => ({
	borderRadius: theme.project.borders.radius.small,
	color: theme.project.colors.fontColors.main,
	justifyContent: "flex-start",
	padding: "10px 12px",
	textTransform: "none"
}));
